import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import PlanLockedHint from "../components/PlanLockedHint";
import RefundModal from "../components/RefundModal";
import Toast from "../components/Toast";
import { param, useListUrlState, useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranches } from "../services/branchService";
import { formatDateTime } from "../services/datetime";
import { hasFeature } from "../services/features";
import { exportPayments, fetchPayments } from "../services/paymentService";
import { can } from "../services/permissions";
import { fetchProviders } from "../services/providerService";
import { labelFor } from "../services/enumLabels";

// `redeemed` is a loyalty reward used from Loyalty → Redeem with no payment.
const STATUSES = ["succeeded", "partially_refunded", "refunded", "pending", "failed", "redeemed"];
// Every method a transaction can carry. loyalty_reward is never picked at the
// till; it marks a reward-covered payment and a standalone reward redemption.
const METHODS = ["cash", "card_present", "card_online", "wallet", "transfer", "other", "loyalty_reward"];
const SETTLEMENTS = ["pending", "settled", "not_applicable"];

const FILTERS = {
  search: param.string("", { debounce: 400 }),
  status: param.oneOf(STATUSES),
  // Any of these; empty = every method. ?methods=cash,loyalty_reward
  methods: param.list(),
  settlementStatus: param.oneOf(SETTLEMENTS),
  branch: param.string(),
  // Only meaningful with a branch; a lone `staff` in the URL is dropped.
  staff: param.string(),
  from: param.date(),
  to: param.date(),
};

/**
 * BIZ-19 — every transaction, with the money broken out.
 *
 * ## The totals are the point of the screen
 *
 * A page of twenty rows cannot answer "what did we take this month?", so the
 * API computes gross, fees, net and refunds across the *whole* filtered set and
 * they sit above the table. Change a filter and they change with it — that is
 * the number somebody came here for, and the rows are the evidence for it.
 *
 * ## Why fee and net are often blank
 *
 * The provider's fee arrives with the settlement, days after the capture. Until
 * then the columns are empty rather than guessed, because a guessed fee is
 * something somebody would try to reconcile against a bank statement.
 */
export default function Transactions() {
  const { t, i18n } = useTranslation();

  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState(null);
  // Page, filters and search live in the URL — see useUrlState.
  const { page, setPage, filters, applyFilters, resetFilters, searchTerm, setSearchTerm, submitSearch } =
    useListUrlState(FILTERS);
  const [branches, setBranches] = useState([]);
  // null = not loaded for this branch yet (also: no branch chosen).
  const [providers, setProviders] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(false);
  // The provider list needs staff.view; without it the filter is hidden, not broken.
  // /me carries the effective set (implied reads included), so this decides up
  // front; a 403 from the list below is only the fallback.
  const [providersForbidden, setProvidersForbidden] = useState(
    () => !can(getCurrentUser(), "staff.view")
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [refunding, setRefunding] = useState(null);
  const [toast, setToast] = useState(null);

  const user = getCurrentUser();
  const mayRefund = can(user, "payments.create");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { payments: rows, meta: pagination } = await fetchPayments({ page, filters });
      setPayments(rows);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("transactions.loadFailed")));
      setPayments([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, filters, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchBranches(1, 100)
      .then(({ branches: list }) => setBranches(list))
      .catch(() => setBranches([]));
  }, [i18n.language]);

  // The providers who work at the chosen branch. A 403 means this user may
  // see takings but not staff, so the filter disappears instead of erroring.
  useEffect(() => {
    setProviders(null);
    if (!filters.branch || providersForbidden) return undefined;

    let cancelled = false;
    setProvidersLoading(true);
    fetchProviders({ perPage: 100, filters: { branch: filters.branch } })
      .then(({ providers: list }) => !cancelled && setProviders(list))
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setProvidersForbidden(true);
        setProviders([]);
      })
      .finally(() => !cancelled && setProvidersLoading(false));

    return () => {
      cancelled = true;
    };
  }, [filters.branch, providersForbidden, i18n.language]);

  // Back from a payment's booking lands where the user was; a stale branch falls back.
  useScrollRestoration(!loading);
  useValidOption(filters.branch, branches.length ? branches : null, () =>
    applyFilters({ branch: "", staff: "" })
  );
  // A provider who does not work at this branch (or a stale link) falls back too.
  useValidOption(filters.staff, providers, () => applyFilters({ staff: "" }));

  // A provider without a branch is ignored, and cleared from the URL.
  useEffect(() => {
    if (filters.staff && (!filters.branch || providersForbidden)) applyFilters({ staff: "" });
  }, [filters.staff, filters.branch, providersForbidden, applyFilters]);

  const filtersApplied = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );
  // A hand-edited URL keeps only the methods that exist.
  const selectedMethods = (filters.methods ?? []).filter((m) => METHODS.includes(m));
  const toggleMethod = (method) =>
    applyFilters({
      methods: selectedMethods.includes(method)
        ? selectedMethods.filter((m) => m !== method)
        : [...selectedMethods, method],
    });

  const runExport = async () => {
    setExporting(true);
    try {
      const filename = await exportPayments(filters);
      setToast({ type: "success", message: t("transactions.exported", { filename }) });
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("transactions.exportFailed")) });
    } finally {
      setExporting(false);
    }
  };

  const totals = meta?.totals;
  // One line per currency (Reports v2, G01): amounts in different currencies
  // are never added up, and never labelled with the first row's symbol. An
  // older API without `by_currency` falls back to its single flat total.
  const perCurrency = totals?.by_currency?.length
    ? totals.by_currency
    : totals
      ? [{ ...totals, currency: totals.currency ?? payments[0]?.currency?.code ?? "" }]
      : [];
  const amounts = (key) =>
    perCurrency.map((entry) => (
      <span key={entry.currency || "none"} dir="ltr" className="block">
        {entry.currency} {entry[key] ?? "0.00"}
      </span>
    ));

  /** succeeded / refunded / failed each read differently at a glance. */
  const statusBadge = (status) => {
    const tones = {
      succeeded: "bg-accent-wash text-accent-text",
      partially_refunded: "bg-warning-wash text-warning-text",
      refunded: "bg-raised text-ink-2",
      pending: "bg-accent-wash text-accent-text",
      failed: "bg-danger-wash text-danger-text",
      redeemed: "bg-accent-wash text-accent-text",
    };

    return (
      <span className={`rounded-full px-3 py-1 text-xs font-medium ${tones[status] ?? tones.pending}`}>
        {labelFor("payment_status", status)}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("transactions.title")}</h1>
          <p className="text-sm text-ink-2">
            {totals ? t("transactions.count", { count: totals.count }) : t("transactions.subtitle")}
          </p>
        </div>

        {/* CSV export is the plan's reports_export (403 plan_feature_unavailable without it). */}
        {hasFeature(user, "reports_export") ? (
          <button type="button" className="btn-secondary" disabled={exporting} onClick={runExport}>
            {exporting ? t("transactions.exporting") : t("transactions.export")}
          </button>
        ) : (
          <PlanLockedHint feature="reports_export" label={t("transactions.export")} />
        )}
      </div>

      {/* Gross, fees, net, refunded — across everything the filters match. */}
      {totals && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {/* gross / tax (inside what was collected) / fees / net / refunded */}
          {["gross", "tax", "fees", "net", "refunded"].map((key) => (
            <div key={key} className="card p-4">
              <p className="text-xs uppercase tracking-wide text-ink-2">
                {t(`transactions.totals.${key}`)}
              </p>
              <p className="text-lg font-semibold">{amounts(key)}</p>
              <p className="text-xs text-ink-2">{t(`transactions.totalsHint.${key}`)}</p>
            </div>
          ))}
          {/* Rewards are not revenue: counted apart from the money above. */}
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-ink-2">{t("transactions.totals.rewards")}</p>
            <p className="text-lg font-semibold">{amounts("rewards_value")}</p>
            <p className="text-xs text-ink-2">
              {t("transactions.totalsHint.rewards", { count: totals.rewards_count ?? 0 })}
            </p>
          </div>
          {perCurrency.length > 1 && (
            <p className="text-xs text-ink-2 sm:col-span-3 lg:col-span-6">{t("transactions.perCurrency")}</p>
          )}
        </div>
      )}

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="card mb-4 flex flex-wrap items-end gap-3 p-4"
      >
        <div className="min-w-[200px] flex-1">
          <label className="field-label" htmlFor="tx-search">
            {t("transactions.filters.search")}
          </label>
          <input
            id="tx-search"
            className="field-input"
            placeholder={t("transactions.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="tx-status">
            {t("transactions.filters.status")}
          </label>
          <select
            id="tx-status"
            className="field-input"
            value={filters.status}
            onChange={(e) => applyFilters({ status: e.target.value })}
          >
            <option value="">{t("transactions.filters.any")}</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {labelFor("payment_status", status)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="tx-settlement">
            {t("transactions.filters.settlement")}
          </label>
          <select
            id="tx-settlement"
            className="field-input"
            value={filters.settlementStatus}
            onChange={(e) => applyFilters({ settlementStatus: e.target.value })}
          >
            <option value="">{t("transactions.filters.any")}</option>
            {SETTLEMENTS.map((option) => (
              <option key={option} value={option}>
                {labelFor("settlement_status", option)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="tx-branch">
            {t("transactions.filters.branch")}
          </label>
          <select
            id="tx-branch"
            className="field-input"
            value={filters.branch}
            // A new branch has a different set of providers.
            onChange={(e) => applyFilters({ branch: e.target.value, staff: "" })}
          >
            <option value="">{t("transactions.filters.any")}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        {!providersForbidden && (
          <div>
            <label className="field-label" htmlFor="tx-staff">
              {t("transactions.filters.provider")}
            </label>
            <select
              id="tx-staff"
              className="field-input disabled:cursor-not-allowed disabled:opacity-60"
              value={filters.branch ? filters.staff : ""}
              disabled={!filters.branch || providersLoading}
              title={filters.branch ? undefined : t("transactions.filters.providerNeedsBranch")}
              onChange={(e) => applyFilters({ staff: e.target.value })}
            >
              <option value="">
                {filters.branch && providersLoading
                  ? t("common.loading")
                  : t("transactions.filters.allProviders")}
              </option>
              {(providers ?? []).map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="tx-from">
            {t("transactions.filters.from")}
          </label>
          <input
            id="tx-from"
            type="date"
            className="field-input"
            value={filters.from}
            onChange={(e) => applyFilters({ from: e.target.value })}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="tx-to">
            {t("transactions.filters.to")}
          </label>
          <input
            id="tx-to"
            type="date"
            className="field-input"
            value={filters.to}
            onChange={(e) => applyFilters({ to: e.target.value })}
          />
        </div>

        {/* Payment method: any of these. Empty = all. */}
        <div className="basis-full">
          <span className="field-label" id="tx-methods-label">
            {t("transactions.filters.paymentMethod")}
          </span>
          <div role="group" aria-labelledby="tx-methods-label" className="flex flex-wrap gap-1.5">
            <button
              type="button"
              aria-pressed={selectedMethods.length === 0}
              onClick={() => applyFilters({ methods: [] })}
              className={`h-8 rounded-full border px-3 text-xs font-medium transition-colors ${
                selectedMethods.length === 0
                  ? "border-accent bg-accent-wash text-accent-text"
                  : "border-line bg-surface text-ink-2 hover:border-line-strong"
              }`}
            >
              {t("transactions.filters.allMethods")}
            </button>
            {METHODS.map((method) => {
              const on = selectedMethods.includes(method);
              return (
                <button
                  key={method}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleMethod(method)}
                  className={`h-8 rounded-full border px-3 text-xs font-medium transition-colors ${
                    on
                      ? "border-accent bg-accent-wash text-accent-text"
                      : "border-line bg-surface text-ink-2 hover:border-line-strong"
                  }`}
                >
                  {labelFor("payment_method", method)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2">
          <button type="submit" className="btn-primary">
            {t("transactions.filters.apply")}
          </button>
          {filtersApplied && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                resetFilters();
              }}
            >
              {t("transactions.filters.clear")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : payments.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {filtersApplied ? t("transactions.noMatches") : t("transactions.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("transactions.table.reference")}</th>
                  <th className="px-4 py-3">{t("transactions.table.client")}</th>
                  <th className="px-4 py-3">{t("transactions.table.items")}</th>
                  <th className="px-4 py-3">{t("transactions.table.method")}</th>
                  <th className="px-4 py-3">{t("transactions.table.gross")}</th>
                  <th className="px-4 py-3">{t("transactions.table.reward")}</th>
                  <th className="px-4 py-3">{t("transactions.table.tax")}</th>
                  <th className="px-4 py-3">{t("transactions.table.paid")}</th>
                  <th className="px-4 py-3">{t("transactions.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => {
                  const isRedemption = payment.kind === "reward_redemption";
                  const money = (value) => (
                    <span dir="ltr" className="tabular-nums">
                      {payment.currency?.symbol} {value}
                    </span>
                  );

                  return (
                  <tr key={`${payment.kind ?? "payment"}-${payment.id}`} className="border-b border-line last:border-0 align-top">
                    <td className="px-4 py-3">
                      <p className="font-medium" dir="ltr">
                        {payment.reference}
                      </p>
                      <p className="text-xs text-ink-2">
                        {payment.paid_at ? formatDateTime(payment.paid_at, i18n.language) : "—"}
                      </p>
                      {isRedemption && (
                        <p className="text-xs text-ink-2">{t("transactions.kind.reward_redemption")}</p>
                      )}
                      {payment.appointment && (
                        <Link
                          to={`/appointments/${payment.appointment.id}`}
                          className="text-xs font-medium text-accent-text hover:underline"
                        >
                          {payment.appointment.reference}
                        </Link>
                      )}
                    </td>

                    <td className="px-4 py-3 text-ink-2">
                      {payment.client ? (
                        <Link
                          to={`/clients/${payment.client.id}`}
                          className="font-medium text-accent-text hover:underline"
                        >
                          {payment.client.name}
                        </Link>
                      ) : (
                        <span className="text-xs text-ink-2">{t("transactions.walkIn")}</span>
                      )}
                      {payment.branch && <p className="text-xs text-ink-2">{payment.branch.name}</p>}
                    </td>

                    {/* What it was for, and who did it. */}
                    <td className="max-w-[16rem] px-4 py-3 text-ink-2">
                      <p className="truncate text-ink" title={(payment.items ?? []).join(", ")}>
                        {(payment.items ?? []).length ? payment.items.join(", ") : "—"}
                      </p>
                      {(payment.providers ?? []).length > 0 && (
                        <p className="truncate text-xs" title={payment.providers.join(", ")}>
                          {payment.providers.join(", ")}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-3 text-ink-2">
                      {(payment.methods ?? [payment.method]).map((method) => (
                        <span key={method} className="block whitespace-nowrap">
                          {labelFor("payment_method", method)}
                        </span>
                      ))}
                    </td>

                    <td className="px-4 py-3">{money(payment.gross ?? payment.amount)}</td>

                    {/* The reward: its name, what it took off, and whether a
                        refund has given it back. */}
                    <td className="px-4 py-3">
                      {payment.reward ? (
                        <div className={payment.reward.status === "returned" ? "opacity-70" : ""}>
                          <span className="inline-flex max-w-[12rem] items-center gap-1 rounded-full bg-accent-wash px-2 py-0.5 text-xs font-medium text-accent-text">
                            <span aria-hidden="true">★</span>
                            <span className="truncate">{t("transactions.rewardBadge", { name: payment.reward.name ?? "" })}</span>
                          </span>
                          <p className={`mt-1 text-xs ${payment.reward.status === "returned" ? "text-ink-2 line-through" : "text-accent-text"}`}>
                            − {money(payment.discount ?? payment.reward.value)}
                          </p>
                          {payment.reward.status === "returned" && (
                            <p className="text-xs text-ink-2">{t("transactions.rewardReturned")}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-ink-2">—</span>
                      )}
                    </td>

                    {/* The tax inside the payment, frozen when it was taken. */}
                    <td className="px-4 py-3 text-ink-2">
                      {Number(payment.breakdown?.tax_amount) > 0 ? money(payment.breakdown.tax_amount) : "—"}
                    </td>

                    <td className="px-4 py-3 font-medium">
                      {money(payment.amount)}
                      {/* Blank until the provider settles and says what it took. */}
                      {(payment.fee_amount || payment.net_amount) && (
                        <p className="text-xs font-normal text-ink-2">
                          {t("transactions.feeNet", {
                            fee: payment.fee_amount ?? "—",
                            net: payment.net_amount ?? "—",
                          })}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {statusBadge(payment.status)}
                      {Number(payment.amount_refunded) > 0 && (
                        <p className="mt-1 text-xs text-ink-2">
                          {t("transactions.refundedAmount", { amount: payment.amount_refunded })}
                        </p>
                      )}
                      {!isRedemption && payment.settlement_status && (
                        <p className="mt-1 text-xs text-ink-2">
                          {labelFor("settlement_status", payment.settlement_status)}
                        </p>
                      )}
                    </td>

                    <td className="px-4 py-3 text-end">
                      {!isRedemption && mayRefund && Number(payment.refundable) > 0 && (
                        <button
                          type="button"
                          className="text-sm font-medium text-accent-text hover:underline"
                          onClick={() => setRefunding(payment)}
                        >
                          {t("transactions.refund")}
                        </button>
                      )}
                      {/* A 0.00 payment a reward covered has nothing to refund;
                          voiding it is how the reward is given back. */}
                      {!isRedemption &&
                        mayRefund &&
                        Number(payment.amount) === 0 &&
                        payment.loyalty_redemption_id &&
                        payment.status === "succeeded" && (
                          <button
                            type="button"
                            className="text-sm font-medium text-accent-text hover:underline"
                            onClick={() => setRefunding(payment)}
                          >
                            {t("transactions.voidReward")}
                          </button>
                        )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <RefundModal
        payment={refunding}
        open={Boolean(refunding)}
        onClose={() => setRefunding(null)}
        onRefunded={(refund) => {
          setToast({
            type: "success",
            message: t("transactions.refunded", { amount: refund.amount }),
          });
          load();
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
